// src/features/purchasing2/components/SendPanel.jsx
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Purchasing2Api, generateIdempotencyKey } from '../api';

export function SendPanel({ purchaseOrders, bootstrapData, onPOsUpdated }) {
  const [selectedMethod, setSelectedMethod] = useState('email');
  const [sendingPOs, setSendingPOs] = useState(new Set());
  const queryClient = useQueryClient();

  // Send PO mutation
  const sendPOMutation = useMutation(
    ({ poId, method }) =>
      Purchasing2Api.send(poId, method, generateIdempotencyKey()),
    {
      onMutate: ({ poId }) => {
        setSendingPOs(prev => new Set(prev).add(poId));
      },
      onSuccess: () => {
        queryClient.invalidateQueries('purchasing2-bootstrap');
        onPOsUpdated([]); // Clear selection
      },
      onSettled: (_, __, { poId }) => {
        setSendingPOs(prev => {
          const newSet = new Set(prev);
          newSet.delete(poId);
          return newSet;
        });
      },
    }
  );

  const handleSendPO = async (poId) => {
    try {
      await sendPOMutation.mutateAsync({ poId, method: selectedMethod });
    } catch (error) {
      console.error('Error sending PO:', error);
      alert('Failed to send purchase order');
    }
  };

  const readyPOs = purchaseOrders.filter(po => po.status === 'READY_TO_SEND');

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Send Purchase Orders</h2>
      
      {/* Send Method Selection */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Send Method
        </label>
        <select
          value={selectedMethod}
          onChange={(e) => setSelectedMethod(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="email">Email Only</option>
          <option value="heritage">Heritage API Only</option>
          <option value="both">Both Email & Heritage</option>
        </select>
      </div>

      {/* Ready to Send Orders */}
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          Ready to Send ({readyPOs.length})
        </h3>
        
        {readyPOs.length > 0 ? (
          <div className="space-y-2">
            {readyPOs.map((po) => (
              <div
                key={po.id}
                className="p-3 border border-gray-200 rounded-md"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium">{po.po}</span>
                    <span className="text-sm text-gray-500 ml-2">- {po.vendorName || po.vendor}</span>
                  </div>
                  <button
                    onClick={() => handleSendPO(po.id)}
                    disabled={sendingPOs.has(po.id)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {sendingPOs.has(po.id) ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-4">No orders ready to send</div>
        )}
      </div>

      {/* Status Messages */}
      {sendPOMutation.isError && (
        <div className="mt-4 text-sm text-red-600">
          Failed to send purchase order
        </div>
      )}
      {sendPOMutation.isSuccess && (
        <div className="mt-4 text-sm text-green-600">
          Purchase order sent successfully!
        </div>
      )}
    </div>
  );
}
